<template>
  <b-modal
    :visible="visible"
    title="Trocar dispositivos"
    size="lg"
    centered
    @hidden="$emit('close')"
    @show="load"
  >
    <p class="text-muted mb-2">
      A atribuição continua ativa. Quem sair fica no histórico de dispositivos, com a data, quem tirou e o motivo.
    </p>

    <label class="mb-0 small text-muted">Dispositivos no veículo</label>
    <v-select
      v-model="form.device_ids"
      :options="options"
      :reduce="option => option.id"
      label="display"
      multiple
      :disabled="loading"
      :placeholder="loading ? 'Carregando dispositivos...' : 'Selecione os dispositivos'"
    />

    <div v-if="added.length || removed.length" class="swap-summary mt-2">
      <span v-for="id in added" :key="'add-' + id" class="badge badge-light-success mr-1">
        <i class="fas fa-plus" /> {{ labelOf(id) }}
      </span>
      <span v-for="id in removed" :key="'rem-' + id" class="badge badge-light-danger mr-1">
        <i class="fas fa-minus" /> {{ labelOf(id) }}
      </span>
    </div>

    <div v-if="form.device_ids.length > 1" class="form-row mt-3">
      <div class="col-md-8">
        <label class="mb-0 small text-muted d-block">Principal (o que escreve a posição do veículo)</label>
        <b-form-radio
          v-for="id in form.device_ids"
          :key="'primary-' + id"
          v-model="form.primary_device_id"
          :value="id"
          name="primary-device"
        >
          {{ labelOf(id) }}
        </b-form-radio>
      </div>
      <div class="col-md-4">
        <label class="mb-0 small text-muted">Raio de desprendimento (m)</label>
        <b-form-input v-model.number="form.detachment_radius_meters" type="number" min="1" size="sm" />
      </div>
    </div>

    <div class="mt-3">
      <label class="mb-0 small text-muted">Motivo da troca</label>
      <b-form-textarea
        v-model="form.reason"
        rows="2"
        maxlength="255"
        placeholder="Ex.: celular com tela quebrada"
      />
    </div>

    <template #modal-footer>
      <b-button variant="secondary" @click="$emit('close')">
        Cancelar
      </b-button>
      <b-button variant="primary" :disabled="saving || loading || !changed" @click="save">
        {{ saving ? 'Salvando...' : 'Salvar troca' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import axios from 'axios'
import vSelect from 'vue-select'
import {
  BModal, BButton, BFormRadio, BFormInput, BFormTextarea,
} from 'bootstrap-vue'

export default {
  name: 'AssignmentDevicesModal',
  components: {
    vSelect, BModal, BButton, BFormRadio, BFormInput, BFormTextarea,
  },
  props: {
    visible: { type: Boolean, default: false },
    assignment: { type: Object, default: null },
    vehicleId: { type: [String, Number], required: true },
  },
  data() {
    return {
      options: [],
      loading: false,
      saving: false,
      original: { device_ids: [], primary_device_id: null, detachment_radius_meters: null },
      form: {
        device_ids: [], primary_device_id: null, detachment_radius_meters: null, reason: '',
      },
    }
  },
  computed: {
    added() {
      return this.form.device_ids.filter(id => !this.original.device_ids.includes(id))
    },
    removed() {
      return this.original.device_ids.filter(id => !this.form.device_ids.includes(id))
    },
    changed() {
      return this.added.length > 0
        || this.removed.length > 0
        || this.form.primary_device_id !== this.original.primary_device_id
        || this.form.detachment_radius_meters !== this.original.detachment_radius_meters
    },
  },
  watch: {
    'form.device_ids': function onDevicesChange(ids) {
      if (!ids.includes(this.form.primary_device_id)) {
        this.form.primary_device_id = ids.length ? ids[0] : null
      }
    },
  },
  methods: {
    labelOf(id) {
      const option = this.options.find(item => item.id === id)
      return option ? option.display : `#${id}`
    },
    deviceLabel(device) {
      const identifier = device.imei || device.serial_number || device.mac_address || device.id
      return device.description ? `${device.description} - ${identifier}` : String(identifier)
    },
    async load() {
      const current = (this.assignment && this.assignment.devices) || []
      const primary = current.find(device => device.pivot && device.pivot.is_primary)
      this.original = {
        device_ids: current.map(device => Number(device.id)),
        primary_device_id: primary ? Number(primary.id) : null,
        detachment_radius_meters: primary && primary.pivot.detachment_radius_meters
          ? Number(primary.pivot.detachment_radius_meters) : null,
      }
      this.form = { ...this.original, device_ids: [...this.original.device_ids], reason: '' }
      this.options = current.map(device => ({ id: Number(device.id), display: this.deviceLabel(device) }))

      this.loading = true
      try {
        const response = await axios.get(`${this.$store.state.api}vehicle_assignments/available-devices`, {
          params: { vehicle_id: this.vehicleId, center_id: this.assignment.center_id || undefined },
        })
        const available = (response.data && (response.data.data || response.data)) || []
        available.forEach(device => {
          if (!this.options.some(option => option.id === Number(device.id))) {
            this.options.push({ id: Number(device.id), display: this.deviceLabel(device) })
          }
        })
      } catch (error) {
        this.$error(error)
      } finally {
        this.loading = false
      }
    },
    async save() {
      this.saving = true
      try {
        await axios.put(`${this.$store.state.api}vehicle_assignments/${this.assignment.id}/devices`, {
          device_ids: this.form.device_ids,
          primary_device_id: this.form.primary_device_id,
          detachment_radius_meters: this.form.device_ids.length > 1 ? this.form.detachment_radius_meters || null : null,
          reason: this.form.reason || null,
        })
        this.$message(this.$t('Success'), 'Dispositivos atualizados. A atribuição continua ativa.', 'success')
        this.$emit('saved')
        this.$emit('close')
      } catch (error) {
        this.$error(error)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.swap-summary .badge {
  font-weight: 500;
}
</style>
